import { gql, GraphQLClient } from 'graphql-request'
import orderBy from 'lodash/orderBy'
import { Env, isServerRuntime, sourceFor } from 'config/constants/subgraphs'
import { multiChainBlocksClient, MultiChainNameExtend } from 'state/info/constant'
import { Block } from '../state/info/types'
import { getGQLHeaders } from './graphql'
import { createEndpointBreaker, describeError } from './endpointBreaker'

/**
 * Availability of the BSC blocks subgraph (`BLOCKS_CLIENT`). The old Studio 50708 deployment was deleted
 * (`deployment u50708/s91984/v263303 does not exist`). Our own `cadinu-blocks` (Studio 101091, published to The Graph
 * Network 2026-09-30) is still syncing — about 25 % of the chain on 2026-09-30, its newest block is from 2023 — so it
 * stays switched off (SUBGRAPH_BLOCKS_SOURCE, config/constants/subgraphs.ts) and every caller sees "unavailable":
 * on the server `getBlocksFromTimestamps` answers `[]` without a request, in a browser the proxy answers 503.
 * Either way the app must degrade instead of failing: once the endpoint proved dead the breaker opens for the session
 * and every caller gets `[]` immediately (no 3 s waits, no thrown errors); `blocksSource.reset()` is the manual retry.
 * Swap charts resolve blocks on the chain instead (`getBlocksFromTimestampsViaChain`, /api/blocks).
 */
export const blocksSource = createEndpointBreaker({
  name: 'blocks-subgraph',
  failuresToOpen: 2,
  cooldownMs: 60_000,
  // the proxy's own answer while the source is off (site review 59 #6, 2026-10-08): open at once, no second request, no retry this session
  permanentErrorPatterns: [/switched off/i],
})

/** Server side only: the environment says the blocks subgraph is switched off. */
const blocksSwitchedOff = () => isServerRuntime() && sourceFor('blocks', process.env as Env) === 'off'

const getBlockSubqueries = (timestamps: number[]) =>
  timestamps.map((timestamp) => {
    return `t${timestamp}:blocks(first: 1, orderBy: timestamp, orderDirection: desc, where: { timestamp_gt: ${timestamp}, timestamp_lt: ${
      timestamp + 600
    } }) {
      number
    }`
  })

const blocksQueryConstructor = (subqueries: string[]) => {
  return gql`query blocks {
    ${subqueries}
  }`
}


const BLOCKS_REQUEST_TIMEOUT_MS = 8_000

const fetchBlockSubqueries = async (subqueries: string[], endpoint: string, skipCount: number) => {
  const client = new GraphQLClient(endpoint, { headers: getGQLHeaders(endpoint) })
  let fetched: Record<string, any> = {}
  for (let skip = 0; skip < subqueries.length; skip += skipCount) {
    const slice = subqueries.slice(skip, skip + skipCount)
    // eslint-disable-next-line no-await-in-loop
    const result = await Promise.race([
      client.request<Record<string, any>>(blocksQueryConstructor(slice)),
      new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error(`blocks subgraph timeout ${BLOCKS_REQUEST_TIMEOUT_MS}ms`)), BLOCKS_REQUEST_TIMEOUT_MS)
      }),
    ])
    fetched = { ...fetched, ...result }
  }
  return fetched
}

/**
 * @notice Fetches block objects for an array of timestamps.
 * Returns [] when the blocks source is unavailable (never throws for that reason).
 * @param {Array} timestamps
 */
export const getBlocksFromTimestamps = async (
  timestamps: number[],
  sortDirection: 'asc' | 'desc' | undefined = 'desc',
  skipCount: number | undefined = 500,
  chainName: MultiChainNameExtend | undefined = 'BSC',
): Promise<Block[]> => {
  if (timestamps?.length === 0) {
    return []
  }

  if (chainName === 'BSC' && (blocksSource.isOpen() || blocksSwitchedOff())) {
    return []
  }
  // a cold /farms load asks twice at once (two hooks): the second caller waits for the first answer, then re-checks the breaker, so a
  // switched-off source costs one request and one debug line, never two 503s in the console (site review #6, fix-r1)
  if (chainName === 'BSC' && inflight) {
    await inflight.catch(() => undefined)
    if (blocksSource.isOpen()) return []
  }
  const run = fetchBlocks(timestamps, sortDirection, skipCount, chainName)
  if (chainName === 'BSC') {
    inflight = run
    run.finally(() => { if (inflight === run) inflight = null }).catch(() => undefined)
  }
  return run
}
let inflight: Promise<Block[]> | null = null

const fetchBlocks = async (timestamps: number[], sortDirection: 'asc' | 'desc' | undefined, skipCount: number | undefined, chainName: MultiChainNameExtend | undefined): Promise<Block[]> => {
  let fetchedData: any = null
  let requestError: unknown
  try {
    fetchedData = await fetchBlockSubqueries(getBlockSubqueries(timestamps), multiChainBlocksClient[chainName], skipCount)
  } catch (error) {
    requestError = error
    // an expected "switched off" answer is not an error worth the console; anything else is a warning, once (the breaker then opens)
    if (/switched off/i.test(describeError(error))) console.debug('blocks subgraph is switched off; charts that need it stay empty')
    else console.warn('Failed to fetch blocks', error)
  }

  const blocks: Block[] = []
  if (fetchedData) {
    // eslint-disable-next-line no-restricted-syntax
    for (const key of Object.keys(fetchedData)) {
      if (fetchedData[key].length > 0) {
        blocks.push({
          timestamp: key.split('t')[1],
          number: parseInt(fetchedData[key][0].number, 10),
        })
      }
    }
    if (chainName === 'BSC') {
      if (blocks.length > 0) {
        blocksSource.recordSuccess()
      } else {
        blocksSource.recordFailure(new Error('blocks subgraph returned no blocks'))
      }
    }
    // graphql-request does not guarantee same ordering of batched requests subqueries, hence manual sorting
    return orderBy(blocks, (block) => block.number, sortDirection)
  }
  if (chainName === 'BSC') {
    blocksSource.recordFailure(requestError ?? new Error('blocks subgraph request failed'))
  }
  return blocks
}

/**
 * Same contract as getBlocksFromTimestamps, but resolved on the chain (BSC) instead of the deleted
 * blocks subgraph: the browser asks /api/blocks (server bisects over eth_getBlockByNumber and caches);
 * on the server it calls the resolver directly. Other chains fall back to the subgraph path.
 * Throws when the resolver fails, so callers can show the reason.
 */
export const getBlocksFromTimestampsViaChain = async (
  timestamps: number[],
  sortDirection: 'asc' | 'desc' | undefined = 'desc',
  skipCount: number | undefined = 500,
  chainName: MultiChainNameExtend | undefined = 'BSC',
): Promise<Block[]> => {
  if (!timestamps?.length) return []
  if (chainName !== 'BSC') return getBlocksFromTimestamps(timestamps, sortDirection, skipCount, chainName)
  let rows: { timestamp: number; number: number }[]
  if (typeof window === 'undefined') {
    const [{ findBlocksByTimestamps }, { getReadClient }] = await Promise.all([
      import('server/info/blocks'),
      import('server/info/rpc'),
    ])
    const found = await findBlocksByTimestamps(getReadClient(), timestamps)
    rows = found.map((b, i) => ({ timestamp: timestamps[i], number: b.number }))
  } else {
    const res = await fetch(`/api/blocks?ts=${timestamps.join(',')}`)
    if (!res.ok) throw new Error(`block resolver HTTP ${res.status}`)
    rows = await res.json()
  }
  return orderBy(
    rows.map((r) => ({ timestamp: String(r.timestamp), number: r.number })),
    (b) => b.number,
    sortDirection,
  )
}
